import React from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';

interface PermissionGuardProps {
  permission: Permission;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Declarative component for permission-based rendering
 *
 * Only renders children if the user has the required permission,
 * otherwise renders the fallback (or nothing if no fallback is provided).
 *
 * @param permission - The permission to check
 * @param fallback - Optional fallback to render if permission is denied
 * @param children - Content to render if permission is granted
 */
export function PermissionGuard({
  permission,
  fallback = null,
  children
}: PermissionGuardProps) {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// Convenience components for common use cases

interface ConditionalRenderProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Only renders children if user is a system administrator
 */
export function AdminOnly({ children, fallback }: ConditionalRenderProps) {
  return (
    <PermissionGuard permission={Permission.ACCESS_SUPER_ADMIN} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * Only renders children if user can modify the layout
 * View-only mode is automatically enforced.
 */
export function LayoutEditOnly({ children, fallback }: ConditionalRenderProps) {
  return (
    <PermissionGuard permission={Permission.MODIFY_LAYOUT} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * Only renders children if user can use favorites
 */
export function FavoritesOnly({ children, fallback }: ConditionalRenderProps) {
  return (
    <PermissionGuard permission={Permission.USE_FAVORITES} fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}